/**
 * NOTE: 年末調整（YearEndAdjustment）デモ用 mock の正データ・導出ビルダー。
 * デモ（`ENABLE_YEAR_END_ADJUSTMENT_OPERATION_PACKAGE`）専用で、実 BE 連携の
 * 正規 YEA とは mock id（`isMockYeaXxxId`）で区別する。実 BE 連携が全環境で
 * 安定したら本ファイルごと削除する。
 *
 * 内部では書類別ステータスを `documents` レコードで保持し、一覧（items[]）・
 * 集約テーブル・ドロワー書類（instance documents）といった各契約形状へ導出する。
 */
import type { GovernmentApplicationAggregateTableRowData } from '@/features/labor-core/-components/government-application-aggregate/types';
import type { PackageInstanceTagResponse } from '@/features/operation-packages/api';
import { apiClient } from '@/lib/api/client';
import { getDataOrThrowApiError } from '@/lib/api/error';
import type { GetYearEndAdjustmentInstanceDocumentsOutput, ListYearEndAdjustmentInstancesByTagOutput, ListYearEndAdjustmentInstancesByTagRowOutput, YearEndAdjustmentDocumentItemKey, YearEndAdjustmentDocumentStatus, YearEndAdjustmentOverallStatus } from '@/lib/api/orpc/contracts/labor-core-year-end-adjustment-instances';
import { MOCK_YEA_TAG_ID } from './yearEndAdjustmentMockIds';

/** 確認画面 `YearEndAdjustmentDocumentProgressCell` の列順（カタログ順）と一致させる */
export const MOCK_YEA_DOCUMENT_STEPS = [{
  id: 'mock-yea-step-dependent-deduction',
  key: 'dependentDeduction',
  name: '扶養'
}, {
  id: 'mock-yea-step-spouse-deduction',
  key: 'spouseDeduction',
  name: '配偶者'
}, {
  id: 'mock-yea-step-insurance-premium',
  key: 'insurancePremium',
  name: '保険料'
}, {
  id: 'mock-yea-step-housing-loan',
  key: 'housingLoan',
  name: '住宅ローン'
}, {
  id: 'mock-yea-step-previous-employer',
  key: 'previousEmployer',
  name: '前職・他社'
}] as const satisfies ReadonlyArray<{
  id: string;
  key: YearEndAdjustmentDocumentItemKey;
  name: string;
}>;
const MOCK_TAG_CREATED_AT = '2025-11-01T00:00:00.000Z';
const MOCK_TAG_UPDATED_AT = '2025-11-15T00:00:00.000Z';
const MOCK_YEA_ANCHOR_DATE = '2025-12-01';
const MOCK_YEA_TAG = {
  id: MOCK_YEA_TAG_ID,
  name: '2025年 年末調整'
} as const;
export const MOCK_YEA_PACKAGE_INSTANCE_TAG = {
  id: MOCK_YEA_TAG_ID,
  name: MOCK_YEA_TAG.name,
  createdAt: MOCK_TAG_CREATED_AT,
  updatedAt: MOCK_TAG_UPDATED_AT
} as const satisfies PackageInstanceTagResponse;
type DocumentStatus = YearEndAdjustmentDocumentStatus;
type StepStatus = NonNullable<GovernmentApplicationAggregateTableRowData['columns'][number]['stepStatus']>;
type MockYeaTarget = ListYearEndAdjustmentInstancesByTagRowOutput['target'];

/**
 * デモの正データ。書類別ステータスは itemKey ごとの `documents` レコードで保持し、
 * 各契約形状（一覧 / 集約 / ドロワー書類）へはここから導出する。
 */
export type MockYeaCanonicalInstance = {
  operationPackageInstanceId: string;
  target: MockYeaTarget;
  overallStatus: YearEndAdjustmentOverallStatus;
  documents: Record<YearEndAdjustmentDocumentItemKey, DocumentStatus>;
  /** システム外（紙等）で別途対応する書類キー（一覧の「別途対応」列 mock 用。未指定は対象なし） */
  separateHandlingDocuments?: readonly YearEndAdjustmentDocumentItemKey[];
  dueDate: string | null;
  lastUpdatedAt: string | null;
};

/** 確認画面 mock の正（一覧・集約・ドロワーはここから導出する） */
export const MOCK_YEA_CANONICAL_INSTANCES = [{
  operationPackageInstanceId: 'mock-yea-inst-1',
  target: {
    targetType: 'Person',
    targetId: 'mock-person-1',
    targetName: '田中 太郎'
  },
  overallStatus: 'completed',
  documents: {
    dependentDeduction: 'confirmed',
    spouseDeduction: 'confirmed',
    insurancePremium: 'confirmed',
    previousEmployer: 'confirmed',
    housingLoan: 'confirmed'
  },
  dueDate: null,
  lastUpdatedAt: '2025-11-20T10:00:00.000Z'
}, {
  operationPackageInstanceId: 'mock-yea-inst-2',
  target: {
    targetType: 'Person',
    targetId: 'mock-person-2',
    targetName: '佐藤 花子'
  },
  overallStatus: 'awaitingReview',
  documents: {
    dependentDeduction: 'submitted',
    spouseDeduction: 'submitted',
    insurancePremium: 'submitted',
    previousEmployer: 'notSubmitted',
    housingLoan: 'submitted'
  },
  dueDate: '2025-12-01',
  lastUpdatedAt: '2025-11-19T10:00:00.000Z'
}, {
  operationPackageInstanceId: 'mock-yea-inst-3',
  target: {
    targetType: 'Person',
    targetId: 'mock-person-3',
    targetName: '鈴木 次郎'
  },
  overallStatus: 'returned',
  documents: {
    dependentDeduction: 'returned',
    spouseDeduction: 'submitted',
    insurancePremium: 'notApplicable',
    previousEmployer: 'notSubmitted',
    housingLoan: 'notApplicable'
  },
  separateHandlingDocuments: ['dependentDeduction', 'spouseDeduction'],
  dueDate: '2025-12-01',
  lastUpdatedAt: '2025-11-18T10:00:00.000Z'
}, {
  operationPackageInstanceId: 'mock-yea-inst-4',
  target: {
    targetType: 'Person',
    targetId: 'mock-person-4',
    targetName: '高橋 三郎'
  },
  overallStatus: 'pendingAction',
  documents: {
    dependentDeduction: 'notSubmitted',
    spouseDeduction: 'notSubmitted',
    insurancePremium: 'notSubmitted',
    previousEmployer: 'notApplicable',
    housingLoan: 'notApplicable'
  },
  dueDate: '2025-12-01',
  lastUpdatedAt: '2025-11-17T10:00:00.000Z'
}, {
  operationPackageInstanceId: 'mock-yea-inst-5',
  target: {
    targetType: 'Person',
    targetId: 'mock-person-5',
    targetName: '伊藤 美咲'
  },
  overallStatus: 'returned',
  documents: {
    dependentDeduction: 'submitted',
    spouseDeduction: 'submitted',
    insurancePremium: 'returned',
    previousEmployer: 'submitted',
    housingLoan: 'submitted'
  },
  separateHandlingDocuments: ['insurancePremium'],
  dueDate: '2025-12-01',
  lastUpdatedAt: '2025-11-16T10:00:00.000Z'
}, {
  operationPackageInstanceId: 'mock-yea-inst-6',
  target: {
    targetType: 'Person',
    targetId: 'mock-person-6',
    targetName: '渡辺 健'
  },
  overallStatus: 'pendingAction',
  documents: {
    dependentDeduction: 'submitted',
    spouseDeduction: 'notApplicable',
    insurancePremium: 'notSubmitted',
    previousEmployer: 'notApplicable',
    housingLoan: 'notSubmitted'
  },
  dueDate: '2025-12-01',
  lastUpdatedAt: '2025-11-15T10:00:00.000Z'
}, {
  operationPackageInstanceId: 'mock-yea-inst-7',
  target: {
    targetType: 'Person',
    targetId: 'mock-person-7',
    targetName: '山本 さやか'
  },
  overallStatus: 'completed',
  documents: {
    dependentDeduction: 'confirmed',
    spouseDeduction: 'notApplicable',
    insurancePremium: 'confirmed',
    previousEmployer: 'notApplicable',
    housingLoan: 'confirmed'
  },
  dueDate: null,
  lastUpdatedAt: '2025-11-14T10:00:00.000Z'
}] as const satisfies ReadonlyArray<MockYeaCanonicalInstance>;
let mockYeaRealTargetsPromise: Promise<readonly MockYeaTarget[] | null> | undefined;

/**
 * mock 対象者を表示先環境（stg / demo）の実メンバーで解決する。
 * アバター URL はドメイン・テナント ID を含み環境ごとに異なるため、
 * FE にハードコードせず workers-for-select API の先頭メンバーから取得する。
 * 取得失敗時は null を返し、呼び出し側は canonical の静的 target を使う。
 */
function fetchMockYeaRealTargets(): Promise<readonly MockYeaTarget[] | null> {
  mockYeaRealTargetsPromise ??= apiClient.GET('/v2/workers-for-select', {
    params: {
      query: {
        page: 1,
        pageSize: MOCK_YEA_CANONICAL_INSTANCES.length
      }
    }
  }).then(res => {
    const workers = getDataOrThrowApiError(res).items;
    if (workers.length === 0) {
      return null;
    }
    return MOCK_YEA_CANONICAL_INSTANCES.map((_, index): MockYeaTarget => {
      const worker = workers[index % workers.length];
      return {
        targetType: 'Person',
        targetId: worker.personId,
        targetName: worker.name,
        targetAvatarUrl: worker.avatarUrl
      };
    });
  }).catch(() => {
    // 失敗時は静的 target で表示し、次回アクセス時に再試行する
    mockYeaRealTargetsPromise = undefined;
    return null;
  });
  return mockYeaRealTargetsPromise;
}

/** canonical 行の target を実環境メンバーに差し替えた一覧（取得失敗時は静的 target のまま） */
export async function resolveMockYeaInstancesWithRealTargets(): Promise<MockYeaCanonicalInstance[]> {
  const targets = await fetchMockYeaRealTargets();
  return MOCK_YEA_CANONICAL_INSTANCES.map((row, index) => {
    const cloned = structuredClone(row) as MockYeaCanonicalInstance;
    const target = targets?.[index];
    return target == null ? cloned : {
      ...cloned,
      target: {
        ...target
      }
    };
  });
}
function documentStatusToStepStatus(status: DocumentStatus): StepStatus | null {
  switch (status) {
    case 'confirmed':
      return 'Completed';
    case 'submitted':
      return 'InProgress';
    case 'returned':
      return 'Abort';
    case 'notSubmitted':
      return 'NotStarted';
    case 'notApplicable':
      return null;
    default:
      {
        const _exhaustive: never = status;
        return _exhaustive;
      }
  }
}
function countApplicableDocuments(row: MockYeaCanonicalInstance): number {
  return MOCK_YEA_DOCUMENT_STEPS.filter(step => row.documents[step.key] !== 'notApplicable').length;
}
function countConfirmedDocuments(row: MockYeaCanonicalInstance): number {
  return MOCK_YEA_DOCUMENT_STEPS.filter(step => row.documents[step.key] === 'confirmed').length;
}
function buildAggregateColumns(row: MockYeaCanonicalInstance): GovernmentApplicationAggregateTableRowData['columns'] {
  return MOCK_YEA_DOCUMENT_STEPS.map(step => {
    const documentStatus = row.documents[step.key];
    const stepStatus = documentStatusToStepStatus(documentStatus);
    return {
      operationStepDefinitionId: step.id,
      hasIncludeFile: documentStatus === 'submitted' || documentStatus === 'returned' || documentStatus === 'confirmed',
      ...(stepStatus != null ? {
        stepStatus
      } : {
        stepStatus: null
      })
    };
  });
}

/** 内部 canonical 行 → 一覧 API 行（書類別ステータスを items[] へ展開） */
function toListRow(row: MockYeaCanonicalInstance): ListYearEndAdjustmentInstancesByTagRowOutput {
  return {
    operationPackageInstanceId: row.operationPackageInstanceId,
    target: structuredClone(row.target),
    overallStatus: row.overallStatus,
    items: MOCK_YEA_DOCUMENT_STEPS.map(step => ({
      itemKey: step.key,
      status: row.documents[step.key],
      separateHandling: row.separateHandlingDocuments?.includes(step.key) ?? false
    })),
    dueDate: row.dueDate,
    lastUpdatedAt: row.lastUpdatedAt
  };
}
export function buildMockYeaConfirmationInstances(rows: readonly MockYeaCanonicalInstance[] = MOCK_YEA_CANONICAL_INSTANCES): ListYearEndAdjustmentInstancesByTagOutput {
  const items = rows.map(toListRow);
  const count = items.length;
  return {
    items,
    length: count,
    count,
    page: 1,
    maxPage: count > 0 ? 1 : 0
  };
}
export function buildMockYeaGovernmentApplicationInstances(rows: readonly MockYeaCanonicalInstance[] = MOCK_YEA_CANONICAL_INSTANCES): {
  items: GovernmentApplicationAggregateTableRowData[];
  count: number;
  length: number;
  page: number;
  maxPage: number;
} {
  const items = rows.map(row => {
    const columns = buildAggregateColumns(row);
    return {
      id: row.operationPackageInstanceId,
      completedOperationStepCount: countConfirmedDocuments(row),
      totalOperationStepCount: countApplicableDocuments(row),
      target: structuredClone(row.target),
      anchorDate: MOCK_YEA_ANCHOR_DATE,
      tag: structuredClone(MOCK_YEA_TAG),
      columns
    };
  });
  const count = items.length;
  return {
    items,
    count,
    length: count,
    page: 1,
    maxPage: count > 0 ? 1 : 0
  };
}

/** mock FORM タスクのインスタンス ID（`{instanceId}-{documentKey}-form`） */
export function buildMockYeaFormTaskInstanceId(instanceId: string, documentKey: YearEndAdjustmentDocumentItemKey): string {
  return `${instanceId}-${documentKey}-form`;
}

/**
 * 確認ドロワー向け 書類一覧 mock（`getYearEndAdjustmentInstanceDocuments` のレスポンス形状）。
 * notApplicable の書類は各 ID を null にしてアコーディオン展開不可とする。
 */
export function buildMockYeaInstanceDocuments(instanceId: string, rows: readonly MockYeaCanonicalInstance[] = MOCK_YEA_CANONICAL_INSTANCES): GetYearEndAdjustmentInstanceDocumentsOutput | undefined {
  const row = rows.find(item => item.operationPackageInstanceId === instanceId);
  if (row == null) {
    return undefined;
  }
  return {
    operationPackageInstanceId: instanceId,
    overallStatus: row.overallStatus,
    items: MOCK_YEA_DOCUMENT_STEPS.map(step => {
      const status = row.documents[step.key];
      const applicable = status !== 'notApplicable';
      return {
        itemKey: step.key,
        status,
        operationStepInstanceId: applicable ? `${instanceId}-${step.key}-step` : null,
        formTaskInstanceId: applicable ? buildMockYeaFormTaskInstanceId(instanceId, step.key) : null,
        // デモの確認/差戻しは DocumentSection 側の no-op override で処理するため
        // 実 mutation を伴わない mock ticketId を付与する（notApplicable は null）。
        confirmationTicketId: applicable ? `${instanceId}-${step.key}-ticket` : null
      };
    })
  };
}

/**
 * サイドバー・パターンタブのバッジ件数。
 * 起動画面テーブルは完了分も含む全インスタンスを表示するため、表示行数（総数）に合わせる。
 */
export const MOCK_YEA_INSTANCE_COUNT = MOCK_YEA_CANONICAL_INSTANCES.length;